import React, { useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';  // Use the routing components here
import LoginPage from './Login/LoginPage';
import { getAuthToken, setAuthToken } from './utils/storageUtil';
import RegisterPage from './Register/RegisterPage';
import HomePage from './Home/HomePage';
import SupportPage from './Support/SupportPage';
import AccountPage from './Account/AccountPage';
import WithdrawalPage from './Withdrawal/WithdrawalPage';
import PasswordPage from './Password/PasswordPage';
import WithdrawalHistoryPage from './WithdrawalHistory/WithdrawalHistoryPage';
import DetailPage from './Detail/DetailPage';
import OrderPage from './Order/OrderPage';
import OrderConfirmationPage from './Order/OrderConfirmationPage';
// import LuckySpinPage from './LuckySpinPage';



/**
 * Routes a signed-out visitor may see. Everything else sends them to /login.
 *
 * /home is public so the bare domain shows the storefront rather than a login
 * form. It still reads a protected endpoint for its product strip — HomePage
 * skips that call when there is no token, because axiosInstance turns a 401
 * into a hard redirect to /login, which would make this page unviewable.
 */
const PUBLIC_ROUTES = new Set(['/', '/home', '/login', '/register']);

const App: React.FC = () => {
  const navigate = useNavigate();  // useNavigate works now because the Router context is available
  const location = useLocation();

  // Admin "login as user" handoff: the admin panel opens this app with the
  // user's token in the query string. Store it, scrub it from the URL so it
  // does not linger in history, then drop into the app as that user.
  useEffect(() => {
    const handoffToken = new URLSearchParams(location.search).get('token');
    if (handoffToken) {
      setAuthToken(handoffToken);
      window.history.replaceState({}, '', window.location.pathname);
      navigate('/home');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-checks on every route change, not just on mount. It terminates:
  // navigating to /login makes the condition false, so it cannot re-fire.
  // The site root is handled declaratively below rather than here, and lands
  // on /home, which is public — so a signed-out visitor to the bare domain
  // now rests on the storefront instead of being bounced to the login form.
  useEffect(() => {
    // Check if the user is logged in by looking for a token in localStorage
    const token = getAuthToken();
    const pathname = location.pathname;
    if (!token && !PUBLIC_ROUTES.has(pathname)) {
      navigate('/login');
    }
  }, [navigate, location.pathname]);

  return (
    <div>
      {/* <h1>{t('welcome')}</h1>
      <p>{t('greeting', { name: 'John' })}</p>
      <div>
        <button onClick={() => i18n.changeLanguage('en')}>English</button>
        <button onClick={() => i18n.changeLanguage('zh')}>中文</button>
        <button onClick={() => i18n.changeLanguage('ms')}>Bahasa Melayu</button>
      </div> */}

      <Routes>
        {/*
          The site root shows the home page. Declared as a route rather than a
          redirect inside the guard effect above: an effect only runs after a
          render in which no route matched "/", which React Router reports as
          "No routes matched location" and which paints an empty frame first.
          `replace` keeps "/" out of history, so Back from /home does not
          return to a location that immediately redirects forward again.
        */}
        <Route path="/" element={<Navigate to="/home" replace />} />

        <Route path="/home" element={<HomePage />} />
        <Route path="/support" element={<SupportPage />} />
        <Route path="/account" element={<AccountPage />} />
        <Route path="/withdrawal" element={<WithdrawalPage />} />
        <Route path="/password" element={<PasswordPage />} />
        <Route path="/withdrawal-history" element={<WithdrawalHistoryPage />} />
        <Route path="/detail" element={<DetailPage />} />
        <Route path="/order" element={<OrderPage />} />
        <Route path="/confirmation" element={<OrderConfirmationPage />} />
        {/* <Route path="/lucky-spin" element={<LuckySpinPage />} /> */}

        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
      </Routes>
    </div>
  );
};

export default App;